import { MinusIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/solid'
import { useState } from 'react'

import { IconButton, Text } from '../../common/designs'
import TrackingPolicyRuleForm from './TrackingPolicyRuleForm'
import type { TrackingPolicyRule } from './types'

interface Props {
  title: string
  description: string
  dotColorClass: string
  rules: TrackingPolicyRule[]
  isSaving: boolean
  onAddRule: (rule: TrackingPolicyRule) => Promise<void>
  onUpdateRule: (index: number, rule: TrackingPolicyRule) => Promise<void>
  onRequestDeleteRule: (index: number) => void
}

const formatRuleLabel = (rule: TrackingPolicyRule) =>
  [rule.in_process_name, rule.in_title, rule.in_url].filter(Boolean).join(' | ')

const TrackingPolicySection = ({
  title,
  description,
  dotColorClass,
  rules,
  isSaving,
  onAddRule,
  onUpdateRule,
  onRequestDeleteRule,
}: Props) => {
  const [openRuleIndex, setOpenRuleIndex] = useState<number | null>(null)
  const [isAddFormOpen, setIsAddFormOpen] = useState(false)

  const closeForms = () => {
    setOpenRuleIndex(null)
    setIsAddFormOpen(false)
  }

  return (
    <section className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
      <div className='flex items-center gap-2'>
        <span className={`size-2.5 rounded-full ${dotColorClass}`} />
        <Text size='l' weight={600}>
          {title}
        </Text>
        <span className='rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600'>
          {rules.length}
        </span>
      </div>
      <Text size='s' color='gray' className='mt-1'>
        {description}
      </Text>

      <div className='mt-4 space-y-2'>
        {rules.length === 0 ? (
          <div className='rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-400'>
            No rules yet.
          </div>
        ) : (
          rules.map((rule, index) => (
            <div key={`${formatRuleLabel(rule)}-${index}`} className='rounded-lg border border-gray-200'>
              <div className='flex min-w-0 items-center gap-2 px-4 py-2'>
                <span className='min-w-0 flex-1 truncate font-medium text-gray-900'>
                  {formatRuleLabel(rule)}
                </span>
                <div className='flex shrink-0 items-center gap-1'>
                  <IconButton
                    variant='ghost'
                    size='s'
                    aria-label={openRuleIndex === index ? 'Close rule editor' : 'Edit rule'}
                    icon={openRuleIndex === index ? <MinusIcon /> : <PlusIcon />}
                    onClick={() => {
                      setIsAddFormOpen(false)
                      setOpenRuleIndex(openRuleIndex === index ? null : index)
                    }}
                  />
                  <IconButton
                    variant='ghost'
                    size='s'
                    aria-label='Delete rule'
                    icon={<TrashIcon />}
                    iconClassName='text-red-500'
                    onClick={() => onRequestDeleteRule(index)}
                  />
                </div>
              </div>
              {openRuleIndex === index && (
                <div className='border-t border-gray-200 px-4 py-3'>
                  <TrackingPolicyRuleForm
                    initialRule={rule}
                    isSaving={isSaving}
                    onSubmit={(updatedRule) => onUpdateRule(index, updatedRule).then(closeForms)}
                  />
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <div className='mt-4'>
        {isAddFormOpen ? (
          <div className='rounded-lg border border-gray-200 p-4'>
            <TrackingPolicyRuleForm
              isSaving={isSaving}
              onSubmit={(rule) => onAddRule(rule).then(closeForms)}
              onCancel={closeForms}
            />
          </div>
        ) : (
          <button
            type='button'
            className='text-sm font-medium text-primary-700 hover:text-primary-800'
            onClick={() => {
              setOpenRuleIndex(null)
              setIsAddFormOpen(true)
            }}
          >
            Add new
          </button>
        )}
      </div>
    </section>
  )
}

export default TrackingPolicySection
